import { createFileRoute, Link } from "@tanstack/react-router";
import { Icon } from "@/components/Icon";

import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/Reveal";
import { MobileStickyBar } from "@/components/MobileStickyBar";
import { AGENCY } from "@/lib/agency";
import { buildHead } from "@/lib/seo";
import aboutHero from "@/assets/about-hero.webp";
import aboutHeroSide from "@/assets/about-hero-side.webp";

const img = (id: string, w = 900) =>
  `https://images.unsplash.com/${id}?auto=format&fit=crop&w=${w}&q=70`;

const IMAGES = {
  hero: aboutHero,
  heroSide: aboutHeroSide,
  approach: img("photo-1573496359142-b8d87734a5a2", 900),
  cta: img("photo-1554224155-6726b3ff858f", 1400),
  industries: {
    "Restaurants & hospitality": img("photo-1517248135467-4c7edcad34c4", 600),
    "Medical & dental": img("photo-1576091160550-2173dba999ef", 600),
    Construction: img("photo-1504307651254-35680f356dfd", 600),
    "Retail & e-commerce": img("photo-1472851294608-062f824d29cc", 600),
    "Trucking & logistics": img("photo-1601584115197-04ecc0da31d7", 600),
    "Auto & repair": img("photo-1486262715619-67b85e0b08d3", 600),
    "Professional services": img("photo-1551836022-d5d88e9218df", 600),
    Manufacturing: img("photo-1565793298595-6a879b1d9492", 600),
  } as Record<string, string>,
};

export const Route = createFileRoute("/about")({
  head: () =>
    buildHead({
      title: `About ${AGENCY.name} - Business Financing Advisors in Dallas`,
      description: `${AGENCY.name} is an independent business financing advisory connecting Dallas-Fort Worth owners with the right loan - straight answers, one dedicated advisor, no upfront fees.`,
      path: "/about",
      image: IMAGES.hero,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "About", path: "/about" },
      ],
    }),
  component: AboutPage,
});

const eyebrow = "text-xs font-semibold uppercase tracking-[0.18em]";

function AboutPage() {
  return (
    <>
      <Hero />
      <WhoWeAre />
      <Approach />
      <Values />
      <WhoWeServe />
      <WhyChooseUs />
      <FinalCTA />
      <MobileStickyBar applyHref="/apply-funding" />
    </>
  );
}

/* ---------------- shared shells ---------------- */

function Band({
  tone = "light",
  id,
  children,
  className = "",
}: {
  tone?: "light" | "soft" | "dark";
  id?: string;
  children: React.ReactNode;
  className?: string;
}) {
  const toneClass =
    tone === "dark"
      ? "bg-primary text-primary-foreground"
      : tone === "soft"
      ? "bg-secondary text-foreground"
      : "bg-background text-foreground";
  return (
    <section
      id={id}
      className={`relative overflow-hidden border-b border-border ${toneClass} ${className}`}
    >
      <div className="relative mx-auto w-full max-w-7xl px-6 py-20 md:py-28 lg:px-10">
        {children}
      </div>
    </section>
  );
}

function SectionHead({
  eyebrow: e,
  title,
  intro,
  dark,
  align = "center",
}: {
  eyebrow: string;
  title: React.ReactNode;
  intro?: string;
  dark?: boolean;
  align?: "center" | "left";
}) {
  const muted = dark ? "text-primary-foreground/65" : "text-muted-foreground";
  const alignCls = align === "left" ? "text-left" : "mx-auto text-center";
  const eyebrowJustify = align === "left" ? "justify-start" : "justify-center";
  return (
    <div className={`${alignCls} max-w-3xl`}>
      <p
        className={`${eyebrow} text-accent flex items-center gap-2 ${eyebrowJustify}`}
      >
        <span className="h-px w-8 bg-current opacity-60" />
        {e}
      </p>
      <h2 className="mt-5 font-serif text-4xl font-semibold leading-[1.05] tracking-tight md:text-5xl">
        {title}
      </h2>
      {intro ? (
        <p
          className={`mt-5 max-w-2xl text-base leading-relaxed md:text-lg ${muted} ${
            align === "center" ? "mx-auto" : ""
          }`}
        >
          {intro}
        </p>
      ) : null}
    </div>
  );
}

/* ---------------- Hero ---------------- */

function Hero() {
  return (
    <section className="relative overflow-hidden bg-primary text-primary-foreground">
      {/* glow */}
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-70"
        style={{
          background:
            "radial-gradient(60% 50% at 15% 20%, color-mix(in oklab, var(--accent) 22%, transparent), transparent 70%), radial-gradient(45% 45% at 85% 80%, color-mix(in oklab, var(--accent) 14%, transparent), transparent 70%)",
        }}
      />
      <div className="relative mx-auto w-full max-w-4xl px-6 py-20 text-center md:py-28 lg:px-10">
        <Reveal>
          <p
            className={`${eyebrow} inline-flex items-center gap-2 text-accent`}
          >
            <span className="h-px w-8 bg-current opacity-60" />
            About {AGENCY.short}
          </p>
          <h1 className="mt-5 font-serif text-5xl font-semibold leading-[1.02] tracking-tight md:text-6xl lg:text-7xl">
            Built to help{" "}
            <span className="text-accent">Dallas business owners</span> get
            the right financing, without the runaround.
          </h1>
          <p className="mx-auto mt-6 max-w-2xl text-lg leading-relaxed text-primary-foreground/80 md:text-xl">
            {AGENCY.name} is an independent business financing advisory.
            We sit on your side of the table, match your goals to the right
            lender, and walk you through every step, so you can stop
            shopping rates and get back to running your company.
          </p>
          <div className="mt-9 flex flex-wrap items-center justify-center gap-3">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-accent px-7 text-accent-foreground hover:bg-accent/90"
            >
              <Link to="/apply-funding">Apply Now</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full border-primary-foreground/30 bg-transparent px-6 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
            >
              <a href={AGENCY.phoneHref}>
                <Icon name="phonecall" color="bl" className="h-4 w-4 brightness-0 invert"  /> Call {AGENCY.phone}
              </a>
            </Button>
          </div>
          <div className="mt-8 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-primary-foreground/75">
            <span className="inline-flex items-center gap-2">
              <Icon name="mappin" color="bl" className="h-4 w-4 text-accent brightness-0 invert"  /> Serving{" "}
              {AGENCY.area}
            </span>
            <span className="inline-flex items-center gap-2">
              <Icon name="shieldcheck" color="bl" className="h-4 w-4 text-accent brightness-0 invert"  /> No upfront fees
            </span>
            <span className="inline-flex items-center gap-2">
              <Icon name="sparkles" color="bl" className="h-4 w-4 text-accent brightness-0 invert"  /> One dedicated
              advisor
            </span>
          </div>
        </Reveal>
      </div>
    </section>
  );
}

/* ---------------- Who we are ---------------- */

function WhoWeAre() {
  const compare = [
    {
      label: "A bank",
      body: "One product, one box. If your business doesn't fit, the answer is usually no, with little explanation of why.",
    },
    {
      label: "An online portal",
      body: "A form that fires your information to dozens of lenders. You get the calls. You sort through the offers. You're on your own.",
    },
    {
      label: `${AGENCY.short}`,
      body: "An independent advisor who learns your business, narrows the field to lenders that actually fit, and stays with you through funding.",
      highlight: true,
    },
  ];

  return (
    <Band tone="light">
      <div className="grid gap-12 lg:grid-cols-12 lg:items-start">
        <div className="lg:col-span-5">
          <SectionHead
            eyebrow="Who we are"
            align="left"
            title={
              <>
                A financing partner, <span className="text-accent">not</span>{" "}
                a bank, not a lead form.
              </>
            }
            intro={`We're an independent advisory team that helps business owners across ${AGENCY.area} find and close the right loan. We're not a lender, and we're not a marketplace that resells your information. We work for you.`}
          />
          <ul className="mt-8 space-y-3 text-base">
            {[
              "We learn your business before we shop your file.",
              "We compare programs across multiple vetted lenders.",
              "We translate offers into plain English so you can decide with confidence.",
            ].map((t) => (
              <li key={t} className="flex items-start gap-3">
                <Icon name="checkcircle2" color="co" className="mt-1 h-5 w-5 shrink-0 text-accent"  />
                <span className="text-foreground/85">{t}</span>
              </li>
            ))}
          </ul>
        </div>

        <div className="lg:col-span-7">
          <div className="grid gap-4 sm:grid-cols-3">
            {compare.map((c, i) => (
              <Reveal key={c.label} delay={i * 80}>
                <div
                  className={[
                    "h-full rounded-2xl border p-6 transition",
                    c.highlight
                      ? "border-accent bg-accent/5 shadow-lg"
                      : "border-border bg-card",
                  ].join(" ")}
                >
                  <p
                    className={`${eyebrow} ${
                      c.highlight ? "text-accent" : "text-muted-foreground"
                    }`}
                  >
                    {c.highlight ? "Us" : "Vs."}
                  </p>
                  <p className="mt-3 font-serif text-xl font-semibold tracking-tight">
                    {c.label}
                  </p>
                  <p className="mt-3 text-sm leading-relaxed text-muted-foreground">
                    {c.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>
    </Band>
  );
}

/* ---------------- Approach ---------------- */

function Approach() {
  const points = [
    {
      icon: "badgecheck",
      title: "One dedicated advisor",
      body: "From the first call to funding, you talk to the same person. No handoffs, no call-center loops.",
    },
    {
      icon: "compass",
      title: "Straight answers",
      body: "We'll tell you what's likely to work, what isn't, and why. Even if the answer is 'not right now.'",
    },
    {
      icon: "shieldcheck",
      title: "No upfront fees",
      body: "We don't charge to review your file or shop offers. You only pay if a lender funds and you accept the terms.",
    },
    {
      icon: "handcoins",
      title: "No pressure",
      body: "Take the offer that's right for your business, or pass. We'd rather earn a long-term relationship than push a one-time deal.",
    },
  ];

  return (
    <Band tone="soft">
      <SectionHead
        eyebrow="How we work"
        title={
          <>
            A simple, <span className="text-accent">human</span> approach to
            business financing.
          </>
        }
        intro="No 30-page applications you have to fill out cold, no opaque underwriting black box, and no surprises at closing."
      />

      <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-4">
        {points.map((p, i) => (
          <Reveal key={p.title} delay={i * 80}>
            <div className="group relative h-full overflow-hidden rounded-2xl border border-border bg-card p-6 transition hover:-translate-y-0.5 hover:shadow-lg">
              <div
                aria-hidden
                className="absolute -right-10 -top-10 h-32 w-32 rounded-full bg-accent/10 blur-2xl transition group-hover:bg-accent/20"
              />
              <div className="relative">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-accent/15 text-accent">
                  <Icon name={p.icon} color="co" className="h-5 w-5"  />
                </span>
                <h3 className="mt-5 font-serif text-xl font-semibold tracking-tight">
                  {p.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-muted-foreground">
                  {p.body}
                </p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </Band>
  );
}

/* ---------------- Values ---------------- */

function Values() {
  const values = [
    {
      icon: "search",
      title: "Transparency",
      body: "Clear terms, plain-language explanations, and an honest read of what an offer really costs over time.",
    },
    {
      icon: "zap",
      title: "Speed when it matters",
      body: "Working capital decisions in hours, not weeks. We move at the speed your business actually operates.",
    },
    {
      icon: "mappin",
      title: "Local focus",
      body: `We work with ${AGENCY.area} owners every day. We know the local industries, lenders, and rhythms.`,
    },
    {
      icon: "handcoins",
      title: "Owner-first",
      body: "Every recommendation is filtered through one question: is this actually right for the business?",
    },
  ];

  return (
    <Band tone="dark">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-0 opacity-60"
        style={{
          background:
            "radial-gradient(50% 50% at 80% 10%, color-mix(in oklab, var(--accent) 18%, transparent), transparent 70%)",
        }}
      />
      <div className="relative">
        <SectionHead
          dark
          eyebrow="What we value"
          title={
            <>
              Principles we <span className="text-accent">don't bend</span>.
            </>
          }
          intro="These aren't slogans. They're the filter for every conversation, every recommendation, and every introduction we make."
        />

        <div className="mt-14 grid gap-px overflow-hidden rounded-3xl bg-primary-foreground/10 sm:grid-cols-2 lg:grid-cols-4">
          {values.map((v, i) => (
            <Reveal key={v.title} delay={i * 80}>
              <div className="h-full bg-primary p-7">
                <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-accent/20 text-accent">
                  <Icon name={v.icon} color="co" className="h-5 w-5"  />
                </span>
                <h3 className="mt-5 font-serif text-xl font-semibold tracking-tight">
                  {v.title}
                </h3>
                <p className="mt-2 text-sm leading-relaxed text-primary-foreground/75">
                  {v.body}
                </p>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </Band>
  );
}

/* ---------------- Who we serve ---------------- */

function WhoWeServe() {
  const industries: Array<{ name: keyof typeof IMAGES.industries; icon: any; blurb: string }> = [
    { name: "Restaurants & hospitality", icon: "utensils", blurb: "Payroll, equipment, and seasonal cash flow." },
    { name: "Medical & dental", icon: "stethoscope", blurb: "Practice expansion, equipment, working capital." },
    { name: "Construction", icon: "hammer", blurb: "Bonding, project capital, equipment financing." },
    { name: "Retail & e-commerce", icon: "shoppingbag", blurb: "Inventory, marketing pushes, fit-outs." },
    { name: "Trucking & logistics", icon: "truck", blurb: "Tractors, trailers, fuel cards, factoring." },
    { name: "Auto & repair", icon: "wrench", blurb: "Lifts, diagnostics, bay expansions." },
    { name: "Professional services", icon: "briefcase", blurb: "Hiring, payroll bridges, office build-outs." },
    { name: "Manufacturing", icon: "building2", blurb: "Equipment, raw materials, plant upgrades." },
  ];

  return (
    <Band tone="light">
      <SectionHead
        eyebrow="Who we serve"
        title={
          <>
            Operators across{" "}
            <span className="text-accent">every kind of Main Street</span>{" "}
            business.
          </>
        }
        intro="If you're a small or mid-sized business in Dallas-Fort Worth, there's a strong chance we've helped someone in your industry."
      />

      <div className="mt-14 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {industries.map((ind, i) => (
          <Reveal key={ind.name} delay={i * 60}>
            <article className="group overflow-hidden rounded-2xl border border-border bg-card transition hover:-translate-y-0.5 hover:shadow-lg">
              <div className="relative aspect-[4/3] overflow-hidden">
                <img
                  src={IMAGES.industries[ind.name]}
                  alt={`${ind.name} business in Dallas-Fort Worth`}
                  loading="lazy"
                  className="h-full w-full object-cover transition duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-primary/70 via-primary/10 to-transparent" />
                <span className="absolute left-4 top-4 inline-flex h-9 w-9 items-center justify-center rounded-lg bg-background/95 text-accent shadow">
                  <Icon name={ind.icon} color="co" className="h-4 w-4"  />
                </span>
                <h3 className="absolute bottom-3 left-4 right-4 font-serif text-lg font-semibold text-primary-foreground">
                  {ind.name}
                </h3>
              </div>
              <p className="p-5 text-sm text-muted-foreground">{ind.blurb}</p>
            </article>
          </Reveal>
        ))}
      </div>

      <p className="mt-10 text-center text-sm text-muted-foreground">
        Don't see your industry? <a href={AGENCY.phoneHref} className="font-semibold text-accent hover:underline">Call us</a>, we likely still have a fit.
      </p>
    </Band>
  );
}

/* ---------------- Why choose us ---------------- */

function WhyChooseUs() {
  const items = [
    {
      icon: "users",
      title: "Real people, on your side",
      body: "An advisor who knows your file by name, not a portal that hands you off to whoever bids highest.",
    },
    {
      icon: "sparkles",
      title: "A wider menu of options",
      body: "Working capital, lines of credit, SBA, equipment, invoice factoring, and same-day programs, all under one roof.",
    },
    {
      icon: "clock",
      title: "Built for speed",
      body: "Most files get a decision the same day or next business day. Funding can follow within 24-72 hours, lender-dependent.",
    },
    {
      icon: "shieldcheck",
      title: "Honest economics",
      body: "We compare true cost of capital, not just rates, so you understand what an offer means for your monthly cash flow.",
    },
    {
      icon: "handcoins",
      title: "Built for the long term",
      body: "We'd rather earn your next loan than win this one. Most of our business comes from repeat owners and referrals.",
    },
    {
      icon: "mappin",
      title: "Rooted in Dallas",
      body: `Headquartered in ${AGENCY.city}, serving owners across ${AGENCY.area}.`,
    },
  ];

  return (
    <Band tone="soft">
      <SectionHead
        eyebrow="Why owners choose us"
        title={
          <>
            What you get when you work with{" "}
            <span className="text-accent">{AGENCY.short}</span>.
          </>
        }
        intro="No silver bullets, no inflated promises. Just a clearer path to the financing decision that's right for your business."
      />

      <div className="mt-14 grid gap-5 md:grid-cols-2 lg:grid-cols-3">
        {items.map((it, i) => (
          <Reveal key={it.title} delay={i * 70}>
            <div className="flex h-full gap-4 rounded-2xl border border-border bg-card p-6">
              <span className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-accent/15 text-accent">
                <Icon name={it.icon} color="co" className="h-5 w-5"  />
              </span>
              <div>
                <h3 className="font-serif text-lg font-semibold tracking-tight">
                  {it.title}
                </h3>
                <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">
                  {it.body}
                </p>
              </div>
            </div>
          </Reveal>
        ))}
      </div>
    </Band>
  );
}

/* ---------------- Final CTA ---------------- */

function FinalCTA() {
  return (
    <section
      id="apply"
      className="relative overflow-hidden border-b border-border bg-primary text-primary-foreground"
    >
      <div
        aria-hidden
        className="absolute inset-0 opacity-30"
        style={{
          backgroundImage: `url(${IMAGES.cta})`,
          backgroundSize: "cover",
          backgroundPosition: "center",
        }}
      />
      <div
        aria-hidden
        className="absolute inset-0"
        style={{
          background:
            "linear-gradient(120deg, color-mix(in oklab, var(--primary) 92%, transparent), color-mix(in oklab, var(--primary) 70%, transparent))",
        }}
      />
      <div className="relative mx-auto max-w-7xl px-6 py-20 md:py-28 lg:px-10">
        <div className="max-w-3xl">
          <p className={`${eyebrow} text-accent flex items-center gap-2`}>
            <span className="h-px w-8 bg-current opacity-60" />
            Ready when you are
          </p>
          <h2 className="mt-5 font-serif text-4xl font-semibold leading-[1.05] tracking-tight md:text-5xl lg:text-6xl">
            Let's find the right loan for your business.
          </h2>
          <p className="mt-5 max-w-2xl text-lg text-primary-foreground/80">
            Talk to an advisor today. No upfront fees, no obligation, no
            hard credit pull to get started.
          </p>
          <div className="mt-9 flex flex-wrap items-center gap-3">
            <Button
              asChild
              size="lg"
              className="rounded-full bg-accent px-7 text-accent-foreground hover:bg-accent/90"
            >
              <Link to="/apply-funding">Apply Now</Link>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className="rounded-full border-primary-foreground/30 bg-transparent px-6 text-primary-foreground hover:bg-primary-foreground/10 hover:text-primary-foreground"
            >
              <a href={AGENCY.phoneHref}>
                <Icon name="phonecall" color="bl" className="h-4 w-4 brightness-0 invert"  /> Call {AGENCY.phone}
              </a>
            </Button>
          </div>
        </div>
      </div>
    </section>
  );
}
